import { presentation } from "./gameplay/defaults";

// apps/client/src/ui/world3d/capability.ts
export const worldCapabilityConfig = {
  NOTICE_STYLE: {
    position: "fixed",
    top: "12px",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: "100",
    padding: "10px 12px",
    background: "#101b20ee",
    color: "#eef2df",
    border: "1px solid #5a6b62",
    font: "13px monospace",
    display: "flex",
    gap: "12px",
    alignItems: "center",
  },
};

// apps/client/src/ui/world3d/shape.ts
export const worldShapeConfig = {
  /** 'teamDark' is the team colour scaled by this. */
  TEAM_DARK: 0.55 as const,
  /** 'teamLight' is the team colour lerped this far toward white. */
  TEAM_LIGHT_WHITE: 0.35 as const,
};

// apps/client/src/ui/world3d/models.ts
export const worldModelsConfig = {
  C: {
    track: 0x1d1e20,
    trackTop: 0x3b3c3f,
    steel: 0x8e939a,
    steelDark: 0x585c63,
    gunmetal: 0x2d3034,
    black: 0x121314,
    concrete: 0xb2ae9e,
    concreteDark: 0x878375,
    slab: 0x7d7a6e,
    tarmac: 0x3d4044,
    tarmacLight: 0x575b61,
    glass: 0x78b8dc,
    gold: 0xe8b02a,
    goldDark: 0xa7740f,
    goldLight: 0xffd75a,
    hazard: 0xf2c21d,
    white: 0xeeeeee,
    red: 0xc0392b,
    skin: 0xd9ad85,
    olive: 0x5d6436,
    pants: 0x3f4433,
    sand: 0xb8a172,
    dirt: 0x6e5c40,
    rock: 0x83725f,
    rockDark: 0x5f5244,
    obsidian: 0x25272d,
    energyGreen: 0x6dff8a,
    energyRed: 0xff3b2f,
    energyBlue: 0x6fd8ff,
    exhaust: 0xffa640,
    amber: 0xffb030,
    orange: 0xd9641c,
    fuel: 0xe0761c,
  },
  // Shared civilian utility paint: architecture never changes with the capturing faction.
  NEUTRAL: {
    hull: 0x818b8d,
    dark: 0x4b575d,
    panel: 0xadb4b1,
    gun: 0x707c80,
    tread: 0x343f45,
    accent: 0xb59a61,
    glow: 0xb59a61,
    lit: false,
  },
};

// apps/client/src/ui/world3d/buildingTraces.ts
export const worldBuildingTracesConfig = {
  BUILDING_TRACE_SECONDS: 3 as const,
  BUILDING_FADE_SECONDS: 2 as const,
  MAX_BUILDING_TRACES: 24 as const,
};

// apps/client/src/ui/world3d/procedural-kit.ts
export const worldProceduralKitConfig = {
  BASTION: {
    hull: 0x879181,
    dark: 0x536268,
    gun: 0x77888d,
    panel: 0xa5aeb0,
    accent: 0x77888d,
    tread: 0x293238,
    glow: 0x9dbdcc,
    lit: false,
  },
  EMBER: {
    hull: 0x788794,
    dark: 0x485d67,
    gun: 0x96a1a5,
    panel: 0x98a5ac,
    accent: 0x956c50,
    tread: 0x303b44,
    glow: 0xd89348,
    lit: true,
  },
  VISOR: 0x15171a as const,
  PLATE: 0x45474c as const,
};

// apps/client/src/ui/world3d/particles.ts
export const worldParticlesConfig = {
  CAMERA_TILT_RAD: 0 as const,
};

// apps/client/src/ui/world3d/scene.ts
export const worldSceneConfig = {
  INFANTRY_IMPACT_TICKS: 24 as const,
};

export const renderingConfig = {
  background: 0x11171a,
  pixelRatioMax: 1.5,
  cloudShadow: { scale: 0.002, speed: [0.012, 0.007], strength: 0.18 },
  terrainNormal: {
    temperate: { frequency: 0.21, strength: 0.12 },
    river: { frequency: 0.18, strength: 0.1 },
    industrial: { frequency: 0.27, strength: 0.08 },
    badlands: { frequency: 0.16, strength: 0.16 },
    coastal: { frequency: 0.19, strength: 0.1 },
    frontier: { frequency: 0.22, strength: 0.12 },
    mesa: { frequency: 0.15, strength: 0.18 },
    alpine: { frequency: 0.25, strength: 0.13 },
    wetlands: { frequency: 0.17, strength: 0.1 },
    caldera: { frequency: 0.28, strength: 0.17 },
  },
  // Large-scale world-space tint that shifts grass/moss/soil between lush
  // green, dry golden and darker damp patches. Scale is world units per noise
  // period; strength is per-biome so alpine/snow and mineral biomes stay quiet.
  groundMacro: {
    scale: 900,
    strength: {
      temperate: 1,
      river: 1,
      industrial: 0.7,
      badlands: 1,
      coastal: 1,
      frontier: 0.4,
      mesa: 0.9,
      alpine: 0.15,
      wetlands: 1.1,
      caldera: 0.5,
    },
  },
  modelCullRadius: 280,
  modelShadowReach: 250,
  propLodDistance: 1800,
  propLodRatio: 0.25,
  // Render-only ground detail. Placement is deterministic per tile; these are
  // calibration knobs, not simulation input.
  groundCover: {
    maxDistance: 1200,
    // Tile is 32 units; smaller cover drops below one pixel at play zoom.
    tuftHeight: 6,
    accentScale: 2.6,
    density: {
      temperate: 0.34,
      river: 0.32,
      industrial: 0.16,
      badlands: 0.12,
      coastal: 0.22,
      frontier: 0,
      mesa: 0.1,
      alpine: 0,
      wetlands: 0.36,
      caldera: 0.08,
    },
  },
  exposure: 1.04,
  hemisphere: { sky: 0xbccbd0, ground: 0x5a4a33, intensity: 1.08 },
  ambient: { color: 0xc0b098, intensity: 0.08 },
  sun: { color: 0xffdcae, intensity: 3.8, offset: { x: -500, y: 1000, z: -650 } },
  environment: {
    intensity: 0.2,
    sphereSegments: 32,
    blur: 0.08,
    horizonStrength: 0.3,
    horizonWidth: 8,
    sunStrength: 1.5,
    sunExponent: 24,
  },
  shadow: {
    size: 2048,
    highSize: 4096,
    normalBias: 0.6,
    bias: -0.00015,
    near: 50,
    far: 4500,
    radius: 3,
    fitPadding: 200,
  },
  fogColor: 0xa3a78e,
  fogDensity: presentation.distanceHaze,
  biomeHaze: {
    temperate: 1,
    river: 1.15,
    industrial: 1.25,
    badlands: 1.15,
    coastal: 1.2,
    frontier: 1,
    mesa: 1.1,
    alpine: 0.9,
    wetlands: 1.25,
    caldera: 1.2,
  },
  bloom: { strength: 0.24, radius: 0.35, threshold: 1.1 },
  grade: {
    lift: [0.018, 0.012, 0.006],
    gamma: 1,
    vignette: 0.14,
    biomes: {
      river: { gain: [1.06, 1, 0.94], saturation: 1.14 },
      industrial: { gain: [1.05, 1, 0.95], saturation: 1.1 },
      badlands: { gain: [1.08, 1.01, 0.92], saturation: 1.18 },
      coastal: { gain: [1.05, 1, 0.95], saturation: 1.14 },
      frontier: { gain: [1.04, 1, 0.96], saturation: 1.1 },
      temperate: { gain: [1.06, 1, 0.94], saturation: 1.15 },
      mesa: { gain: [1.08, 1.01, 0.91], saturation: 1.18 },
      alpine: { gain: [1.03, 1, 0.97], saturation: 1.1 },
      wetlands: { gain: [1.05, 1, 0.94], saturation: 1.16 },
      caldera: { gain: [1.06, 1, 0.92], saturation: 1.14 },
    },
  },
  rangeOverlay: { far: 2400 },
  nametag: {
    near: 520,
    far: 640,
    fontFa: "700 11px Estedad, sans-serif",
    fontEn: '800 italic 11px "Barlow Condensed", sans-serif',
  },
  particles: {
    smoke: 1800,
    fire: 1400,
    rings: 128,
    scorch: 256,
    tracks: 512,
    contact: 2048,
    debris: 300,
  },
  decals: {
    scorchLife: 36000,
    trackLife: 1800,
    trackSpacing: 22,
    trackOffset: 9,
    trackSize: 14,
    trackAlpha: 0.35,
    trackColor: [0.13, 0.12, 0.1],
    height: 0.4,
  },
  polishEffects: {
    muzzleCore: { size: 8, life: 3, r: 5, g: 4, b: 2 },
    muzzleSmoke: {
      size: 7,
      endSize: 20,
      life: 35,
      alpha: 0.45,
      speed: 0.5,
      rise: 0.2,
      shade: 0.32,
    },
    blastCore: { size: 0.7, endSize: 1.5, life: 7, r: 5, g: 3.6, b: 1.5 },
  },
  flashes: { count: 4, color: 0xffa641, distance: 150, decay: 1.5 },
  ghost: { color: 0x7bf3b0, opacity: 0.4 },
  frameEase: 0.05,
  motion: {
    tiltLimit: 0.07,
    turnScale: 0.012,
    accelerationScale: 0.0002,
    smoothing: 7,
    recoilPitch: 0.035,
  },
  // Displayed body yaw damping rate toward the target facing, 1/s (higher turns faster).
  turnRate: 16,
  // Life of a local order turn hint in ms; the server facing can end it sooner.
  turnHintMs: 400,
};

export const graphicsRecoveryConfig = {
  noticeDelayMs: 3000,
};
